/**
 * GalleryFieldType — F12.B T12.B.4.
 * Wiele obrazków przesyłanych do MinIO przez /api/modules/:moduleId/upload.
 * Wartość: TEXT[] kluczy MinIO.
 *
 * renderInput — upload + podgląd miniatur z presigned URL
 * renderCell  — miniaturki w trybie view (lightbox na kliknięcie)
 */
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Images, Loader2, Upload, X, ZoomIn } from 'lucide-react';

import { IconButton } from '@/components/IconButton';
import { Overlay } from '@/components/Overlay';
import { api } from '@/lib/api';

import { FieldRegistry, type ModuleField } from '../FieldRegistry';

// ─── Presigned thumbnail ──────────────────────────────────────────────────────

function PresignedThumb({
  moduleId,
  minioKey,
  alt,
  onRemove,
  onClick,
}: {
  moduleId: string;
  minioKey: string;
  alt: string;
  onRemove?: () => void;
  onClick?: () => void;
}) {
  const { data: url, isLoading } = useQuery({
    queryKey: ['presigned-url', moduleId, minioKey],
    queryFn: async () => {
      const res = await api.get<{ url: string }>(`/modules/${moduleId}/upload/download`, {
        params: { key: minioKey },
      });
      return res.data.url;
    },
    staleTime: 55 * 60 * 1000,
    enabled: !!minioKey,
  });

  return (
    <div className="group relative">
      <div
        className="h-24 w-full overflow-hidden rounded border border-border-primary bg-bg-secondary flex items-center justify-center cursor-pointer"
        onClick={onClick}
      >
        {isLoading ? (
          <Loader2 className="h-5 w-5 animate-spin text-text-muted" />
        ) : url ? (
          <img src={url} alt={alt} className="h-full w-full object-cover" loading="lazy" />
        ) : (
          <Images className="h-6 w-6 text-text-muted opacity-40" />
        )}
        {onClick && !isLoading && url && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded">
            <ZoomIn className="h-5 w-5 text-white" />
          </div>
        )}
      </div>
      {onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="absolute right-1 top-1 rounded-full bg-danger-600 p-0.5 text-white opacity-0 transition-opacity group-hover:opacity-100"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

// ─── Lightbox ─────────────────────────────────────────────────────────────────

function Lightbox({
  moduleId,
  keys,
  initialIndex,
  onClose,
}: {
  moduleId: string;
  keys: string[];
  initialIndex: number;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [idx, setIdx] = useState(initialIndex);
  const { data: url } = useQuery({
    queryKey: ['presigned-url', moduleId, keys[idx]],
    queryFn: async () => {
      const res = await api.get<{ url: string }>(`/modules/${moduleId}/upload/download`, {
        params: { key: keys[idx] },
      });
      return res.data.url;
    },
    staleTime: 55 * 60 * 1000,
    enabled: keys.length > 0,
  });

  // Zachowanie (tło, Esc, fokus, blokada przewijania) daje wspólny rdzeń `Overlay`. Okno jest
  // portalem, ale zdarzenia React bąbelkują przez drzewo komponentów — bez zatrzymania kliknięcie
  // w „X", strzałkę lub zdjęcie trafiłoby do rodzica komórki (np. kliknięcia wiersza tabeli).
  return (
    <div className="contents" onClick={(e) => e.stopPropagation()}>
      <Overlay
        open
        onClose={onClose}
        ariaLabel={t('attachmentPreview.lightboxImage', { n: idx + 1 })}
        testId="gallery-lightbox"
        backdropClassName="z-[200] flex items-center justify-center bg-black/80"
        panelClassName="relative max-h-[90vh] max-w-[90vw]"
      >
        {url ? (
          <img
            src={url}
            alt={`Zdjęcie ${idx + 1}`}
            className="max-h-[85vh] max-w-[85vw] rounded object-contain shadow-2xl"
          />
        ) : (
          <Loader2 className="h-10 w-10 animate-spin text-white" />
        )}
        <IconButton
          size="sm"
          onClick={onClose}
          aria-label={t('common.close')}
          className="absolute -right-3 -top-3 h-auto w-auto rounded-full bg-white p-1 text-black shadow-lg hover:bg-bg-secondary hover:text-black"
        >
          <X aria-hidden="true" />
        </IconButton>
        {keys.length > 1 && (
          <>
            <IconButton
              onClick={() => setIdx((i) => (i - 1 + keys.length) % keys.length)}
              aria-label={t('attachmentPreview.previous')}
              className="absolute left-2 top-1/2 h-auto w-auto -translate-y-1/2 rounded-full bg-white/80 px-2 py-1 text-sm font-bold text-black hover:bg-white hover:text-black"
            >
              ‹
            </IconButton>
            <IconButton
              onClick={() => setIdx((i) => (i + 1) % keys.length)}
              aria-label={t('attachmentPreview.next')}
              className="absolute right-2 top-1/2 h-auto w-auto -translate-y-1/2 rounded-full bg-white/80 px-2 py-1 text-sm font-bold text-black hover:bg-white hover:text-black"
            >
              ›
            </IconButton>
            <p className="absolute bottom-2 left-1/2 -translate-x-1/2 rounded bg-black/60 px-2 py-0.5 text-xs text-white">
              {idx + 1} / {keys.length}
            </p>
          </>
        )}
      </Overlay>
    </div>
  );
}

// ─── renderCell — miniaturki w widoku read-only ───────────────────────────────

function GalleryCellView({ field, keys }: { field: ModuleField; keys: string[] }) {
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
  return (
    <>
      <div className="flex flex-wrap gap-1">
        {keys.slice(0, 4).map((k, i) => (
          <PresignedThumb
            key={k}
            moduleId={field.moduleId}
            minioKey={k}
            alt={`Zdjęcie ${i + 1}`}
            onClick={() => setLightboxIdx(i)}
          />
        ))}
        {keys.length > 4 && (
          <div
            className="flex h-24 w-24 cursor-pointer items-center justify-center rounded border border-border-primary bg-bg-secondary text-sm font-medium text-text-secondary hover:bg-bg-tertiary"
            onClick={() => setLightboxIdx(4)}
          >
            +{keys.length - 4}
          </div>
        )}
      </div>
      {lightboxIdx !== null && (
        <Lightbox
          moduleId={field.moduleId}
          keys={keys}
          initialIndex={lightboxIdx}
          onClose={() => setLightboxIdx(null)}
        />
      )}
    </>
  );
}

// ─── Registration ─────────────────────────────────────────────────────────────

FieldRegistry.register({
  type: 'gallery',
  renderInput: ({ field, value, onChange }) => {
    const images: string[] = Array.isArray(value) ? (value as string[]) : [];
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const remove = (i: number) => onChange(images.filter((_, idx) => idx !== i));

    const handleFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files || files.length === 0) return;
      setError(null);
      setUploading(true);
      try {
        const keys: string[] = [];
        for (const file of Array.from(files)) {
          const fd = new FormData();
          fd.append('file', file);
          fd.append('fieldSlug', field.slug);
          const res = await api.post<{ key: string }>(`/modules/${field.moduleId}/upload`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          keys.push(res.data.key);
        }
        onChange([...images, ...keys]);
      } catch {
        setError('Nie udało się przesłać pliku. Spróbuj ponownie.');
      } finally {
        setUploading(false);
        if (inputRef.current) inputRef.current.value = '';
      }
    };

    return (
      <div className="space-y-3">
        {images.length > 0 && (
          <div className="grid grid-cols-3 gap-2">
            {images.map((minioKey, i) => (
              <PresignedThumb
                key={minioKey}
                moduleId={field.moduleId}
                minioKey={minioKey}
                alt={`Obraz ${i + 1}`}
                onRemove={() => remove(i)}
                onClick={() => setLightboxIdx(i)}
              />
            ))}
          </div>
        )}

        <label className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border-primary px-4 py-3 text-text-secondary hover:border-accent-400 hover:text-accent-600 transition-colors">
          {uploading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Upload className="h-4 w-4" />
          )}
          <span className="text-sm">{uploading ? 'Przesyłanie…' : 'Dodaj zdjęcia'}</span>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFiles}
            className="sr-only"
          />
        </label>

        {error && <p className="text-sm text-danger-500">{error}</p>}

        {lightboxIdx !== null && (
          <Lightbox
            moduleId={field.moduleId}
            keys={images}
            initialIndex={lightboxIdx}
            onClose={() => setLightboxIdx(null)}
          />
        )}
      </div>
    );
  },

  renderCell: (field, value) => {
    const keys = Array.isArray(value) ? (value as string[]).filter(Boolean) : [];
    if (keys.length === 0) return '—';
    return <GalleryCellView field={field} keys={keys} />;
  },

  validate: (value, field) => {
    if (value != null && !Array.isArray(value)) return 'Wartość musi być tablicą';
    if (field.isRequired && (!Array.isArray(value) || value.length === 0))
      return 'Dodaj przynajmniej jeden obraz';
    return null;
  },
  defaultValue: [],
});
